import {
  Label,
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Portal,
  Transition,
} from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { Fragment, ReactNode } from 'react';
import { usePopper } from '../../hooks/usePopper';
import { classNames } from '../../utils';
import Loader from './Loader';

export interface SelectOption<T> {
  key?: string;
  value: T;
  disabled?: boolean;
  child: ReactNode;
}

interface BaseProps<T = unknown> {
  title?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  options: SelectOption<T>[];
  className?: string;
  titlePosition?: 'default' | 'overlapping';
}

interface SingleProps<T> extends BaseProps<T> {
  multi?: false;
  selected?: T;
  onChange?: (e: T) => void;
}

interface MultiProps<T> extends BaseProps<T> {
  multi: true;
  selected?: T[];
  onChange?: (e: T[]) => void;
}

type Props<T> = SingleProps<T> | MultiProps<T>;

export default function Select<T>({
  multi,
  title,
  placeholder,
  selected,
  disabled,
  loading,
  onChange,
  options,
  className,
  titlePosition,
}: Props<T>) {
  const [trigger, container] = usePopper({
    placement: 'bottom-end',
    strategy: 'fixed',

    modifiers: [
      {
        name: 'matchWidth',
        enabled: true,
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
          state.styles.popper.maxWidth = `${state.rects.reference.width}px`;
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
      },
      {
        name: 'offset',
        options: {
          offset: [0, 10],
        },
      },
    ],
  });

  return (
    <Listbox
      as="div"
      value={selected}
      onChange={onChange}
      disabled={disabled}
      multiple={multi}
      className="relative"
    >
      {({ open, value }) => (
        <>
          {title ? (
            <Label
              className={classNames(
                titlePosition === 'overlapping'
                  ? 'absolute -top-2 left-2 z-10 inline-block bg-white px-1 text-xs font-medium text-gray-900'
                  : 'block text-sm font-medium leading-6 text-gray-900',
              )}
            >
              {title}
            </Label>
          ) : (
            <></>
          )}
          <div
            className={classNames(
              'relative',
              title && titlePosition !== 'overlapping' ? 'mt-2' : '',
            )}
          >
            <ListboxButton
              className={classNames(
                value ? 'text-gray-900' : 'text-gray-400',
                'relative min-h-[36px] w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-600 disabled:bg-gray-100 disabled:text-gray-400 disabled:ring-gray-200 sm:text-sm sm:leading-6',
                className,
              )}
              ref={trigger}
            >
              <span className="block">
                {multi
                  ? Array.isArray(value) && value.length
                    ? options
                        .filter((opt) => value.includes(opt.value))
                        .map((opt) => opt.child)
                        ?.join(', ')
                    : placeholder
                  : value
                    ? options.find((opt) => opt.value === value)?.child
                    : placeholder}
              </span>
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                {loading ? <Loader size={4} /> : <></>}
                <ChevronUpDownIcon
                  className="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </ListboxButton>

            <Portal>
              <Transition
                show={open}
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <ListboxOptions
                  ref={container}
                  className="z-10 mt-1 max-h-40 w-40 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                >
                  {options.map((option) => (
                    <ListboxOption
                      key={option.key || option.value + ''}
                      disabled={option.disabled}
                      className={({ active }) =>
                        classNames(
                          active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                          'relative cursor-default select-none py-2 pl-3 pr-9',
                        )
                      }
                      value={option.value}
                    >
                      {({ selected, active, disabled }) => (
                        <>
                          <span
                            className={classNames(
                              disabled ? 'text-gray-400' : '',
                              selected ? 'font-semibold' : 'font-normal',
                              'block truncate',
                            )}
                          >
                            {option.child}
                          </span>

                          {selected ? (
                            <span
                              className={classNames(
                                active ? 'text-white' : 'text-indigo-600',
                                'absolute inset-y-0 right-0 flex items-center pr-4',
                              )}
                            >
                              <CheckIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                              />
                            </span>
                          ) : null}
                        </>
                      )}
                    </ListboxOption>
                  ))}
                </ListboxOptions>
              </Transition>
            </Portal>
          </div>
        </>
      )}
    </Listbox>
  );
}
